import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';

/**
 * Arquivar, restaurar e excluir uma reunião (card 3786). Funções puras, com spec isolado: a linha da
 * lista e o cabeçalho do detalhe montam o menu com elas, e as duas telas confirmam com os mesmos textos.
 *
 * - **Arquivar / Restaurar**: `MEETING_ARCHIVE`. Arquivada sai das listas e do card da Home e fica só na
 *   aba Arquivadas; restaurar volta tudo como estava.
 * - **Excluir**: `MEETING_DELETE` **e** `canDelete` do servidor (quem criou a reunião, ou ADMIN). Apaga
 *   gravação e transcrição e não tem volta.
 */
export type MeetingLifecycleAction = 'archive' | 'restore' | 'delete';

/** Permissões de quem está logado (claims), vindas do `PermissionsService`. */
export interface MeetingLifecyclePermissions {
  canArchive: boolean;
  canDelete: boolean;
}

/** Um item de menu, sem depender do PrimeNG (quem monta o `MenuItem` é o componente). */
export interface MeetingLifecycleOption {
  action: MeetingLifecycleAction;
  label: string;
  icon: string;
  /** Ação destrutiva: o menu pode separá-la das demais. */
  danger: boolean;
}

/** Textos da confirmação e do toast de cada ação. */
export interface MeetingLifecycleTexts {
  title: string;
  description: string;
  variant: 'default' | 'danger';
  acceptLabel: string;
  successMessage: string;
  failureTitle: string;
}

type MeetingLike = Pick<MeetingDto, 'title' | 'sequentialId' | 'archivedAt' | 'canDelete'>;

export function isMeetingArchived(meeting: Pick<MeetingDto, 'archivedAt'>): boolean {
  return !!meeting.archivedAt;
}

/**
 * As ações que a pessoa pode fazer nesta reunião, na ordem do menu: Arquivar (ou Restaurar, se já
 * arquivada) e, por último, Excluir.
 */
export function meetingLifecycleOptions(
  meeting: MeetingLike,
  permissions: MeetingLifecyclePermissions
): MeetingLifecycleOption[] {
  const options: MeetingLifecycleOption[] = [];
  if (permissions.canArchive) {
    options.push(
      isMeetingArchived(meeting)
        ? { action: 'restore', label: 'Restaurar', icon: 'pi pi-replay', danger: false }
        : { action: 'archive', label: 'Arquivar', icon: 'pi pi-inbox', danger: false }
    );
  }
  if (permissions.canDelete && meeting.canDelete === true) {
    options.push({ action: 'delete', label: 'Excluir', icon: 'pi pi-trash', danger: true });
  }
  return options;
}

/** Nome da reunião nas mensagens: o título, ou "Reunião #123" quando não tem. */
export function meetingDisplayName(meeting: Pick<MeetingDto, 'title' | 'sequentialId'>): string {
  const title = meeting.title?.trim();
  return title || `Reunião #${meeting.sequentialId}`;
}

/** Textos da confirmação e do resultado de cada ação. */
export function meetingLifecycleTexts(
  action: MeetingLifecycleAction,
  meeting: Pick<MeetingDto, 'title' | 'sequentialId'>
): MeetingLifecycleTexts {
  const name = meetingDisplayName(meeting);
  switch (action) {
    case 'archive':
      return {
        title: 'Arquivar esta reunião?',
        description: `"${name}" sai das listas de reuniões e do card de reuniões da Home, para todo o time. A gravação, a transcrição e os vínculos continuam guardados: ela fica na aba Arquivadas, onde dá para restaurar.`,
        variant: 'default',
        acceptLabel: 'Arquivar',
        successMessage: 'Reunião arquivada. Ela está na aba Arquivadas.',
        failureTitle: 'Não foi possível arquivar',
      };
    case 'restore':
      return {
        title: 'Restaurar esta reunião?',
        description: `"${name}" volta para as listas de reuniões e para o card de reuniões da Home, como estava antes de ser arquivada.`,
        variant: 'default',
        acceptLabel: 'Restaurar',
        successMessage: 'Reunião restaurada. Ela voltou para as listas.',
        failureTitle: 'Não foi possível restaurar',
      };
    case 'delete':
      return {
        title: 'Excluir esta reunião?',
        description: `"${name}" é excluída com a gravação e a transcrição, para todo o time. Isso não pode ser desfeito. Se quiser só tirá-la das listas, use Arquivar.`,
        variant: 'danger',
        acceptLabel: 'Excluir',
        successMessage: 'Reunião excluída, com a gravação e a transcrição.',
        failureTitle: 'Não foi possível excluir',
      };
  }
}
